<script>
import { mapActions, mapGetters } from 'vuex';
import { getDocument } from '_acaSrc/utility/DOM';
import { C_LOCALIZATION } from '_acaSrc/utility/constants';

const COLLAPSED = 'pp_collapsed';
const EXPANDED = 'pp_expanded';
const CHEVRON_DOWN = 'wkce-icon-chevron-down';
const CHEVRON_UP = 'wkce-icon-chevron-up';
const VIEW_LESS = 'Read less'; // fallback labels used for rendering panel in kpp/bqp panels
const VIEW_MORE = 'Read more';

export default {
    data() {
        return {
            practicePoints: [],
            contentId: '',
            selectedLanguage: '',
            componentType: ''
        };
    },
    computed: {
        ...mapGetters('topic', {
            topicPracticePointsLabels: 'practicePointsLabels',
            topicContentLanguage: 'contentLanguage',
            topicSelectedTranslationToggleOption: 'selectedTranslationToggleOption'
        }),
        ...mapGetters('search', {
            searchPracticePointsLabels: 'practicePointsLabels'
        }),
        practicePointsLabels() {
            return this.searchPracticePointsLabels || this.topicPracticePointsLabels;
        },
        activeLanguage() {
            return this.selectedLanguage || this.topicContentLanguage;
        },
        shouldDisplayLocalLabels() {
            return (this.selectedLanguage !== C_LOCALIZATION.ENGLISH_CODE)
                || (this.topicSelectedTranslationToggleOption === C_LOCALIZATION.LOCAL_LANGUAGE_ID);
        },
        englishPracticePointsLabels() {
            const englishLabels = {
                viewLessLabel: VIEW_LESS,
                viewMoreLabel: VIEW_MORE
            };
            return this.practicePointsLabels
                && this.practicePointsLabels[C_LOCALIZATION.ENGLISH_CODE]
                || englishLabels;
        },
        localPracticePointsLabels() {
            if (!this.activeLanguage) {
                return this.englishPracticePointsLabels;
            }
            return this.practicePointsLabels
                && this.practicePointsLabels[this.activeLanguage]
                || this.englishPracticePointsLabels;
        },
        viewButtonLabels() {
            if (this.shouldDisplayLocalLabels) {
                return this.localPracticePointsLabels;
            }
            return this.englishPracticePointsLabels;
        }
    },
    beforeUnmount() {
        this.practicePoints.forEach(practicePoint => {
            const viewButton = practicePoint.querySelector('button');
            viewButton && viewButton.removeEventListener('click', this.handleViewMoreClick);
            const externalLinks = practicePoint.querySelectorAll('.external');
            externalLinks.forEach(link => {
                link && link.removeEventListener('click', this.handlePPExternalLinkClick);
            });
        });
    },
    methods: {
        ...mapActions('app', [ 'logEvent' ]),
        setupPracticePoints({
            contentId,
            languageCode,
            source
        }) {
            this.componentType = source;
            this.contentId = contentId;
            this.selectedLanguage = languageCode || C_LOCALIZATION.ENGLISH_CODE;
            this.practicePoints = this.$el.querySelectorAll('.practice-point');
            this.practicePoints.forEach(practicePoint => {
                if (practicePoint.querySelector('.wkce-icon')) {
                    return null;
                }
                this.attachPracticePointListener(practicePoint);
                this.changeButtonStyles(practicePoint);
                this.addLanguageClass(practicePoint);
                const externalLinks = practicePoint.querySelectorAll('.external');
                externalLinks.forEach(link => {
                    this.attachPPLinkClickListener(link, practicePoint);
                });
            });
            return this.practicePoints;
        },
        changeButtonStyles(practicePoint) {
            if (!practicePoint) {
                return null;
            }
            const btn = practicePoint.querySelector('[id*=ppViewMoreBtn_]');
            if (!btn) {
                return null;
            }
            btn.classList.add(
                'utd-button-style-text',
                'utd-button-color-blue',
                'utd-button-size-small');

            this.addIcon(btn);
            return btn;
        },
        addIcon(buttonElement) {
            const icon = getDocument().createElement('span');
            icon.classList.add('wkce-icon', 'wkce-icon-chevron-down');

            buttonElement.appendChild(icon);
            return icon;
        },
        addLanguageClass(practicePoint) {
            // adds a class to the component that specifies the language currently displayed
            // allows for language-specific style overrides
            if (!practicePoint) {
                return;
            }
            const langClass = `pp_lang_${this.selectedLanguage}`;
            practicePoint.classList.add(langClass);
        },
        attachPracticePointListener(practicePoint) {
            if (!practicePoint) {
                return null;
            }
            const viewButton = practicePoint.querySelector('button');
            viewButton.addEventListener('click', this.handleViewMoreClick);
        },
        handleViewMoreClick(event) {
            const clickElement = event && event.target;
            if (!clickElement) {
                return null;
            }
            const expandedContent = clickElement.parentElement
                && clickElement.parentElement.previousElementSibling;
            this.toggleExpandedContent(expandedContent, clickElement);
        },
        toggleExpandedContent(expandedContent, element) {
            let action = 'viewMore';
            if (!expandedContent || !element) {
                return null;
            }
            const btnIcon = element.querySelector('.wkce-icon');
            const btnText = element.querySelector('.practice-point__button-label');
            if (!btnText || !btnIcon) {
                return null;
            }
            if (expandedContent.classList.contains(COLLAPSED)) {
                btnText.textContent = this.viewButtonLabels.viewLessLabel;
                btnIcon.classList.replace(CHEVRON_DOWN, CHEVRON_UP);
                expandedContent.classList.replace(COLLAPSED, EXPANDED);
            }
            else {
                btnText.textContent = this.viewButtonLabels.viewMoreLabel;
                btnIcon.classList.replace(CHEVRON_UP, CHEVRON_DOWN);
                expandedContent.classList.replace(EXPANDED, COLLAPSED);
                action = 'viewLess';
            }
            this.logEvent({
                eventUrl: 'event/machineTranslationClick/json',
                eventParams: {
                    referringUrl: window.location.href,
                    uiElementName: `practicePoints_${action}_${this.componentType}`,
                    optData: element.id,
                    contentId: this.contentId,
                    languageCode: this.selectedLanguage
                }
            });
            return expandedContent;
        },
        attachPPLinkClickListener(externalLink, practicePoint) {
            if (!externalLink) {
                return null;
            }
            externalLink.practicePointId = practicePoint && practicePoint.id;
            externalLink.addEventListener('click', this.handlePPExternalLinkClick);
        },
        handlePPExternalLinkClick(e) {
            this.logEvent({
                eventUrl: 'event/machineTranslationClick/json',
                eventParams: {
                    referringUrl: window.location.href,
                    uiElementName: `practicePointsLink_${this.componentType}`,
                    optData: e && e.target && e.target.practicePointId,
                    contentId: this.contentId,
                    languageCode: this.selectedLanguage
                }
            });
        }
    }
};
</script>